// Gestão de Empresas (Admin)
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Building, AlertCircle, Search, Trash2, X, Users, UserPlus } from 'lucide-react';
import { formatCNPJ } from '../../../utils/formatters';
import { getEmpresas, deleteEmpresa } from '../../../services/empresasService';
import { criarEmpresaEVincularUsuario } from '../../../services/adminService';
import { obterUsuariosComEmpresas } from '../../../services/adminService';
import { useAuth } from '../../../contexts/AuthContext';
import { vincularUsuarioEmpresa, desvincularUsuarioEmpresa, getUsuariosDaEmpresa } from '../../../services/usuariosService';
import type { Empresa } from '../../../types';
import type { UsuarioEmpresa } from '../../../services/usuariosService';

export function AdminEmpresas() {
  const { user } = useAuth();
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCriarModalOpen, setIsCriarModalOpen] = useState(false);
  const [empresaEditando, setEmpresaEditando] = useState<Empresa | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [usuariosEmpresa, setUsuariosEmpresa] = useState<UsuarioEmpresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usuarioParaVincular, setUsuarioParaVincular] = useState('');
  const [roleParaVincular, setRoleParaVincular] = useState<'admin' | 'operador' | 'visualizador'>('visualizador');
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [formData, setFormData] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    inscricaoEstadual: '',
    email: '',
    telefone: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    usuarioId: '',
  });

  useEffect(() => {
    carregarEmpresas();
  }, []);

  const carregarEmpresas = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar todas as empresas
      const empresasData = await getEmpresas();
      setEmpresas(empresasData);

      // Buscar usuários para o select
      const usuariosData = await obterUsuariosComEmpresas();
      setUsuarios(usuariosData);
    } catch (err: any) {
      console.error('[AdminEmpresas] Erro ao carregar empresas:', err);
      setError(err.message || 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleCriarEmpresa = async () => {
    try {
      if (!formData.usuarioId) {
        setError('Selecione um usuário para vincular a empresa');
        return;
      }

      setIsSaving(true);
      setError(null);

      await criarEmpresaEVincularUsuario(
        {
          razaoSocial: formData.razaoSocial,
          nomeFantasia: formData.nomeFantasia,
          cnpj: formData.cnpj,
          inscricaoEstadual: formData.inscricaoEstadual,
          email: formData.email,
          telefone: formData.telefone,
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
        },
        formData.usuarioId
      );

      // Limpar formulário
      setFormData({
        razaoSocial: '',
        nomeFantasia: '',
        cnpj: '',
        inscricaoEstadual: '',
        email: '',
        telefone: '',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        usuarioId: '',
      });

      setIsCriarModalOpen(false);
      await carregarEmpresas();
    } catch (err: any) {
      console.error('[AdminEmpresas] Erro ao criar empresa:', err);
      setError(err.message || 'Erro ao criar empresa');
    } finally {
      setIsSaving(false);
    }
  };

  const empresasFiltradas = empresas.filter((e) =>
    e.nomeFantasia.toLowerCase().includes(busca.toLowerCase()) ||
    e.razaoSocial.toLowerCase().includes(busca.toLowerCase()) ||
    e.cnpj.includes(busca) ||
    e.email.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = async (empresa?: Empresa) => {
    setEmpresaEditando(empresa || null);
    setIsModalOpen(true);
    
    // Se abrir modal de empresa, carregar usuários vinculados
    if (empresa) {
      await carregarUsuariosEmpresa(empresa.id);
    } else {
      setUsuariosEmpresa([]);
    }
    
    setUsuarioParaVincular('');
    setRoleParaVincular('visualizador');
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setEmpresaEditando(null);
    setUsuariosEmpresa([]);
    setUsuarioParaVincular('');
    setRoleParaVincular('visualizador');
  };

  const carregarUsuariosEmpresa = async (empresaId: string) => {
    try {
      setLoadingUsuarios(true);
      const usuariosData = await getUsuariosDaEmpresa(empresaId);
      setUsuariosEmpresa(usuariosData);
    } catch (err: any) {
      console.error('[AdminEmpresas] Erro ao carregar usuários da empresa:', err);
      setUsuariosEmpresa([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const handleVincularUsuario = async () => {
    if (!empresaEditando || !usuarioParaVincular) {
      setError('Selecione um usuário para vincular');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await vincularUsuarioEmpresa(usuarioParaVincular, empresaEditando.id, roleParaVincular);

      setUsuarioParaVincular('');
      setRoleParaVincular('visualizador');
      await carregarUsuariosEmpresa(empresaEditando.id);
      await carregarEmpresas();
    } catch (err: any) {
      console.error('[AdminEmpresas] Erro ao vincular usuário:', err);
      setError(err.message || 'Erro ao vincular usuário');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDesvincularUsuario = async (usuarioId: string) => {
    if (!empresaEditando) return;

    if (!confirm('Tem certeza que deseja desvincular este usuário da empresa?')) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await desvincularUsuarioEmpresa(usuarioId, empresaEditando.id);

      await carregarUsuariosEmpresa(empresaEditando.id);
      await carregarEmpresas();
    } catch (err: any) {
      console.error('[AdminEmpresas] Erro ao desvincular usuário:', err);
      setError(err.message || 'Erro ao desvincular usuário');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExcluirEmpresa = async (empresaId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await deleteEmpresa(empresaId);

      await carregarEmpresas();
      if (empresaEditando?.id === empresaId) {
        fecharModal();
      }
    } catch (err: any) {
      console.error('[AdminEmpresas] Erro ao excluir empresa:', err);
      setError(err.message || 'Erro ao excluir empresa');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Empresas' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Empresas' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestão de Empresas</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerenciar todas as empresas cadastradas na plataforma</p>
        </div>
        <Button variant="primary" onClick={() => setIsCriarModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Busca */}
      <Card>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            label="Buscar Empresa"
            placeholder="Nome fantasia, razão social, CNPJ ou email"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        {empresasFiltradas.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400">Nenhuma empresa encontrada</p>
          </div>
        ) : (
          <Table
            data={empresasFiltradas}
            columns={[
              {
                key: 'nomeFantasia',
                label: 'Nome Fantasia',
                render: (item: Empresa) => (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{item.nomeFantasia}</span>
                  </div>
                ),
              },
              {
                key: 'razaoSocial',
                label: 'Razão Social',
              },
              {
                key: 'cnpj',
                label: 'CNPJ',
                render: (item: Empresa) => formatCNPJ(item.cnpj),
              },
              {
                key: 'email',
                label: 'Email',
              },
              {
                key: 'telefone',
                label: 'Telefone',
              },
              {
                key: 'actions',
                label: 'Ações',
                render: (item: Empresa) => (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => abrirModal(item)}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                      title="Ver detalhes e gerenciar usuários"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleExcluirEmpresa(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Excluir empresa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* Modal de Criação */}
      <Modal
        isOpen={isCriarModalOpen}
        onClose={() => setIsCriarModalOpen(false)}
        title="Nova Empresa"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Razão Social"
              value={formData.razaoSocial}
              onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
              required
            />
            <Input
              label="Nome Fantasia"
              value={formData.nomeFantasia}
              onChange={(e) => setFormData({ ...formData, nomeFantasia: e.target.value })}
              required
            />
            <Input
              label="CNPJ"
              value={formData.cnpj}
              onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
              required
            />
            <Input
              label="Inscrição Estadual"
              value={formData.inscricaoEstadual}
              onChange={(e) => setFormData({ ...formData, inscricaoEstadual: e.target.value })}
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              label="Telefone"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
            />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Endereço</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="CEP"
                value={formData.cep}
                onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
              />
              <Input
                label="Logradouro"
                value={formData.logradouro}
                onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
              />
              <Input
                label="Número"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
              />
              <Input
                label="Complemento"
                value={formData.complemento}
                onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
              />
              <Input
                label="Bairro"
                value={formData.bairro}
                onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
              />
              <Input
                label="Cidade"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
              />
              <Input
                label="Estado"
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                maxLength={2}
              />
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <Select
              label="Vincular ao Usuário"
              value={formData.usuarioId}
              onChange={(e) => setFormData({ ...formData, usuarioId: e.target.value })}
              options={[
                { value: '', label: 'Selecione um usuário' },
                ...usuarios.map((u) => ({
                  value: u.usuarioId,
                  label: `${u.nome} (${u.email})`,
                })),
              ]}
              required
            />
          </div>

          <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={() => setIsCriarModalOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCriarEmpresa} disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Criar Empresa'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Edição */}
      <Modal
        isOpen={isModalOpen}
        onClose={fecharModal}
        title={empresaEditando ? 'Detalhes da Empresa' : 'Nova Empresa'}
        size="lg"
      >
        <div className="space-y-4">
          {empresaEditando && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Razão Social
                </label>
                <p className="text-gray-900 dark:text-gray-100">{empresaEditando.razaoSocial}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Fantasia
                </label>
                <p className="text-gray-900 dark:text-gray-100">{empresaEditando.nomeFantasia}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CNPJ
                </label>
                <p className="text-gray-900 dark:text-gray-100">{formatCNPJ(empresaEditando.cnpj)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <p className="text-gray-900 dark:text-gray-100">{empresaEditando.email}</p>
              </div>

              {/* Seção de Usuários Vinculados */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Usuários Vinculados ({usuariosEmpresa.length})
                </label>
                {loadingUsuarios ? (
                  <div className="text-center py-4">
                    <Loading />
                  </div>
                ) : usuariosEmpresa.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {usuariosEmpresa.map((vinculo) => {
                      const usuario = usuarios.find(u => u.usuarioId === vinculo.usuarioId);
                      return (
                        <div key={vinculo.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {usuario?.nome || usuario?.email || vinculo.usuarioId}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {usuario?.email || 'Email não disponível'} • Role: {vinculo.role}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDesvincularUsuario(vinculo.usuarioId)}
                            disabled={isSaving}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Desvincular usuário"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Nenhum usuário vinculado</p>
                )}

                {/* Seção para vincular novo usuário */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Vincular Novo Usuário
                  </label>
                  <div className="space-y-3">
                    <Select
                      label="Usuário"
                      value={usuarioParaVincular}
                      onChange={(e) => setUsuarioParaVincular(e.target.value)}
                      options={[
                        { value: '', label: 'Selecione um usuário' },
                        ...usuarios
                          .filter(u => !usuariosEmpresa.find(ue => ue.usuarioId === u.usuarioId))
                          .map((u) => ({
                            value: u.usuarioId,
                            label: `${u.nome || u.email} (${u.email})`,
                          })),
                      ]}
                    />
                    <Select
                      label="Role"
                      value={roleParaVincular}
                      onChange={(e) => setRoleParaVincular(e.target.value as 'admin' | 'operador' | 'visualizador')}
                      options={[
                        { value: 'admin', label: 'Admin' },
                        { value: 'operador', label: 'Operador' },
                        { value: 'visualizador', label: 'Visualizador' },
                      ]}
                    />
                    <Button
                      variant="primary"
                      onClick={handleVincularUsuario}
                      disabled={!usuarioParaVincular || isSaving}
                      className="w-full"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Vincular Usuário
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={fecharModal}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

